// <LineageExplorer> — FXC-8538 (Warp).
//
// Focus + expand navigation over a large backing graph. The backing
// graph can hold millions of atomic nodes (every output scalar — CL,
// CD, mesh, geometry); the explorer only ever hands the renderer the
// *revealed* neighbourhood, so the canvas never chokes and the view
// stays meaningful.
//
// Mirrors Thread's lineage UX (v1/v2 `LineageGraphView`): click a node
// to reveal its antecedents (upstream) and descendants (downstream).
// `onSelect` hands the host the clicked node plus its direct neighbours
// so it can render "antecedents / descendants" lists alongside.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  buildIndex,
  directAncestors,
  directDescendants,
  neighborhood,
  subgraph,
  type GraphIndex,
  type LineageDirection,
  type LineageResult,
} from "../core/lineage";
import {
  atomicUnits,
  collapseClusters as collapseClustersTransform,
  communities,
  connectedComponents,
  maximalCliques,
  type Clustering,
} from "../core/cliques";
import { clusterColourFor } from "../core/palette";
import type { Graph, GraphEdge, GraphNode } from "../core/types";
import { ThreadGraph } from "./ThreadGraph";
import type { GraphControls, GraphGroup, GraphRenderer } from "./GraphRenderer";

const COLLAPSED_CLUSTER_ID_PREFIX = "cluster:";
const MAX_CLIQUE_CLUSTERING_NODES = 80;

/** How to cluster the revealed neighbourhood (or "none" to colour by type). */
export type ClusterMode = "none" | "communities" | "components" | "cliques" | "units";

/** Per-cluster summary for a legend. */
export interface ClusterInfo {
  id: number;
  color: string;
  size: number;
  /** Most common node type in the cluster (for a legend label). */
  dominantType: string;
}

export interface LineageSelection {
  node: GraphNode;
  /** Direct (1-hop) antecedents — what this node depends on. */
  antecedents: GraphNode[];
  /** Direct (1-hop) descendants — what depends on this node. */
  descendants: GraphNode[];
  /**
   * The cluster the selected node belongs to under the active `clusterBy`
   * mode, if any — its id, member node ids, and dominant type. Lets a host
   * "capture" that clique/community by extracting its subgraph
   * (`subgraph(graph, new Set(cluster.nodeIds))`). `null` when no cluster mode
   * is active or the node is unclustered.
   */
  cluster: { id: number; nodeIds: string[]; dominantType: string } | null;
}

export interface LineageExplorerProps {
  /** Full backing graph. May be very large — only neighbourhoods render. */
  graph: Graph;
  /** Node to centre on initially. */
  focusId: string;
  /** Renderer name or component. Default "force". */
  renderer?: string | GraphRenderer;
  /**
   * Surface theme — forwarded to the renderer for label/outline contrast.
   * "dark" (default) for a dark canvas; "light" for a light surface (V5).
   */
  theme?: "light" | "dark";
  /** Layout direction for layered renderers (hierarchical). Default "RIGHT". */
  layoutDirection?: "RIGHT" | "DOWN";
  /** Hops revealed around the focus on mount / refocus. Default 1. */
  initialDepth?: number;
  /** Hops revealed each time a node is clicked. Default 1. */
  revealDepth?: number;
  /** Which way a click expands. Default "both". */
  expandDirection?: LineageDirection;
  /** Cluster the revealed neighbourhood and colour/outline by it. Default "none". */
  clusterBy?: ClusterMode;
  /** Scale node radius by degree. Default true (lineage hubs stand out). */
  sizeByDegree?: boolean;
  /** Collapse each cluster in view into a single super-node (structural overview). */
  collapse?: boolean;
  /** Animate flow particles along the selected node's lineage edges. Default true. */
  flowParticles?: boolean;
  /**
   * When to draw node text labels (forwarded to the renderer). `"hover"`
   * (default) labels only the hovered node + the highlighted (selected + 1-hop /
   * search-hit) frontier — at thousands of nodes drawing every label per frame
   * is the dominant render cost and freezes the main thread. `"all"` restores
   * always-on de-cluttered labels for small graphs / surfaces that opt in
   * (e.g. Warp). Nodes stay legible by colour + shape regardless.
   */
  labelMode?: "hover" | "all";
  /**
   * Max NEW nodes revealed per expand (and on the initial focus / re-centre).
   * Caps how many neighbours a single click adds to the canvas — without it,
   * expanding a hub (e.g. a mesh feeding thousands of runs) floods the renderer
   * and janks. The closest nodes (lowest hop distance) are kept. Default 400.
   */
  revealLimit?: number;
  /**
   * Lazy neighbourhood loader for million-node graphs. When provided, the
   * explorer treats `graph` as a SEED (typically the focus's initial
   * neighbourhood) and never traverses a full backing graph to expand: on each
   * reveal it asks the host to fetch the neighbourhood of the clicked node ids
   * (the host calls e.g. the server's `/v2/graph/neighbourhood` endpoint),
   * merges the returned `Graph` into an internal working graph, then reveals
   * those nodes (still bounded by `revealLimit`). The backing graph can be
   * millions of nodes that never load whole. When omitted, reveals are computed
   * locally from `graph` (default, unchanged).
   */
  onRevealRequest?: (nodeIds: string[], depth: number) => Promise<Graph>;
  /** Current backing graph after lazy merges; useful for extracting visible clusters. */
  onGraphChange?: (graph: Graph) => void;
  /**
   * Node-type → colour, forwarded to the renderer (e.g. drive the graph off
   * the Thread V5 `--fc-*` tokens). When a cluster mode is active, cluster
   * colours take precedence per node; this drives the base colouring
   * otherwise. Omit for the built-in palette.
   */
  colourForType?: (type: string) => string;
  /** Edge-predicate → colour, forwarded to the renderer. Omit for the built-in palette. */
  colourForPredicate?: (predicate: string) => string;
  /**
   * Per-node colour override, forwarded to the renderer. An active cluster
   * colour still wins where present; this applies to the remaining nodes
   * (and to every node when `clusterBy="none"`). Omit for type/cluster colouring.
   */
  nodeColor?: (node: GraphNode) => string | undefined;
  /**
   * Search hits to reveal + highlight + focus. The host computes these
   * however it likes (semantic /api/search, FTS, label match) — Warp just
   * surfaces them in the graph. Changing the set reveals the hits + their
   * 1-hop neighbours and centres on the first present hit.
   */
  searchHits?: ReadonlySet<string>;
  /** Called whenever the selected node changes (incl. initial focus). */
  onSelect?: (selection: LineageSelection) => void;
  /** Called with the cluster legend and actual mode whenever clustering recomputes. */
  onClusters?: (clusters: ClusterInfo[], mode: ClusterMode) => void;
  /** Called once the renderer's imperative controls are available. */
  onReady?: (controls: GraphControls | null) => void;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  className?: string;
}

export function LineageExplorer({
  graph,
  focusId,
  renderer = "force",
  theme = "dark",
  layoutDirection,
  initialDepth = 1,
  revealDepth = 1,
  expandDirection = "both",
  clusterBy = "none",
  sizeByDegree = true,
  collapse = false,
  flowParticles = true,
  labelMode = "hover",
  revealLimit = 400,
  onRevealRequest,
  onGraphChange,
  colourForType,
  colourForPredicate,
  nodeColor,
  searchHits,
  onSelect,
  onClusters,
  onReady,
  width,
  height,
  style,
  className,
}: LineageExplorerProps) {
  const lazy = !!onRevealRequest;
  // In lazy mode `graph` is a SEED; reveals fetch neighbourhoods from the host
  // and accumulate into a working graph (the backing graph never loads whole).
  // Otherwise `graph` is the full backing graph (default, unchanged).
  const [workingGraph, setWorkingGraph] = useState<Graph>(graph);
  const backing = lazy ? workingGraph : graph;

  // lineageKey is over the SEED (`graph` prop), not the (growing) working graph,
  // so lazy fetches don't keep retripping the re-centre effect below.
  const lineageKey = useMemo(() => lineageGraphKey(graph), [graph.nodes, graph.edges]);
  const index = useMemo(() => buildIndex(backing), [backing.nodes, backing.edges]);
  const hasFocus = index.nodeById.has(focusId) || (lazy && graph.nodes.some((node) => node.id === focusId));
  const focusReset = useMemo(
    () => ({ lineageKey, focusId, initialDepth, revealLimit }),
    [lineageKey, focusId, initialDepth, revealLimit],
  );

  const [revealState, setRevealState] = useState<RevealState>(
    () => ({
      ids: limitedReveal(neighborhood(index, focusId, { depth: initialDepth }), revealLimit, [focusId]),
      focusReset,
    }),
  );
  const [selected, setSelected] = useState<string>(focusId);

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onRevealRequestRef = useRef(onRevealRequest);
  onRevealRequestRef.current = onRevealRequest;
  const onGraphChangeRef = useRef(onGraphChange);
  onGraphChangeRef.current = onGraphChange;
  const workingGraphRef = useRef(workingGraph);
  workingGraphRef.current = workingGraph;
  const lastSelectionKeyRef = useRef<string | null>(null);
  const mergeWorkingGraph = useCallback((fetched: Graph) => {
    const merged = mergeGraphs(workingGraphRef.current, fetched);
    workingGraphRef.current = merged;
    setWorkingGraph(merged);
    return merged;
  }, []);
  const revealRequestVersionRef = useRef(0);
  const beginRevealRequest = useCallback(() => {
    revealRequestVersionRef.current += 1;
    return revealRequestVersionRef.current;
  }, []);
  const isCurrentRevealRequest = useCallback(
    (version: number) => revealRequestVersionRef.current === version,
    [],
  );

  // Re-centre when the focus prop or seed graph changes (new project/focus).
  useEffect(() => {
    if (sameFocusReset(revealState.focusReset, focusReset)) return;
    // Lazy: reset the working graph to the new seed and reveal over a fresh
    // index of it (the live `index` may still be over the previous seed).
    if (lazy) {
      beginRevealRequest();
      workingGraphRef.current = graph;
      setWorkingGraph(graph);
    }
    lastSelectionKeyRef.current = null;
    const seedIndex = lazy ? buildIndex(graph) : index;
    setRevealState({
      ids: limitedReveal(neighborhood(seedIndex, focusId, { depth: initialDepth }), revealLimit, [focusId]),
      focusReset,
    });
    setSelected(focusId);
  }, [lazy, graph, index, focusId, initialDepth, revealLimit, revealState.focusReset, focusReset, beginRevealRequest]);

  // Search hits → reveal them (+ 1-hop neighbours), highlight, and centre
  // on the first present hit. Host computes the hits (semantic/FTS/label).
  useEffect(() => {
    if (!searchHits || searchHits.size === 0) return;
    const hits = [...searchHits];
    const present = hits.filter((id) => index.nodeById.has(id));
    const missing = hits.filter((id) => !index.nodeById.has(id));
    if (present.length > 0) {
      setRevealState((prev) => {
        return { ...prev, ids: revealNearest(prev.ids, index, present, 1, revealLimit, "both") };
      });
      setSelected(present[0]!);
    }
    if (lazy && missing.length > 0 && onRevealRequestRef.current) {
      let cancelled = false;
      const requestVersion = beginRevealRequest();
      void onRevealRequestRef.current(missing, 1)
        .then((fetched) => {
          if (cancelled || !isCurrentRevealRequest(requestVersion)) return;
          const merged = mergeWorkingGraph(fetched);
          const mergedIndex = buildIndex(merged);
          const mergedPresent = hits.filter((id) => mergedIndex.nodeById.has(id));
          if (mergedPresent.length === 0) return;
          setRevealState((prev) => ({
            ...prev,
            ids: revealNearest(prev.ids, mergedIndex, mergedPresent, 1, revealLimit, "both"),
          }));
          setSelected(mergedPresent[0]!);
        })
        .catch(() => {
          if (cancelled || !isCurrentRevealRequest(requestVersion)) return;
          if (present.length === 0) return;
          setRevealState((prev) => ({
            ...prev,
            ids: revealNearest(prev.ids, index, present, 1, revealLimit, "both"),
          }));
          setSelected(present[0]!);
        });
      return () => { cancelled = true; };
    }
  }, [searchHits, index, revealLimit, lazy, mergeWorkingGraph, beginRevealRequest, isCurrentRevealRequest]);

  const visible = useMemo(() => subgraph(index, revealState.ids), [index, revealState.ids]);
  const effectiveClusterBy: ClusterMode = clusterBy === "cliques" && visible.nodes.length > MAX_CLIQUE_CLUSTERING_NODES
    ? "communities"
    : clusterBy;

  // Cluster the *visible* slice (bounded — never the whole backing graph).
  const clustering = useMemo<Clustering | null>(() => {
    switch (effectiveClusterBy) {
      case "communities": return communities(visible);
      case "components": return connectedComponents(visible);
      case "units": return atomicUnits(visible);
      case "cliques": return cliqueClustering(visible);
      default: return null;
    }
  }, [visible, effectiveClusterBy]);

  const emitSelect = useCallback(
    (id: string) => {
      const node = index.nodeById.get(id);
      const cb = onSelectRef.current;
      if (!node || !cb) return;
      const antecedents = directAncestors(index, id);
      const descendants = directDescendants(index, id);
      const cluster = clusterOf(id, clustering, index.nodeById);
      const key = selectionKey(node, antecedents, descendants, cluster);
      if (lastSelectionKeyRef.current === key) return;
      lastSelectionKeyRef.current = key;
      cb({ node, antecedents, descendants, cluster });
    },
    [index, clustering],
  );

  useEffect(() => {
    onGraphChangeRef.current?.(backing);
  }, [backing]);

  useEffect(() => {
    if (sameFocusReset(revealState.focusReset, focusReset)) emitSelect(selected);
  }, [emitSelect, selected, revealState.focusReset, focusReset]);

  const clusterNodeColor = useMemo(() => {
    if (!clustering) return undefined;
    return (node: GraphNode): string | undefined => {
      const c = clustering.labelOf.get(node.id);
      return c === undefined ? undefined : clusterColourFor(c);
    };
  }, [clustering]);

  const groups = useMemo<GraphGroup[]>(() => {
    if (!clustering) return [];
    const out: GraphGroup[] = [];
    for (const [cid, members] of clustering.clusters) {
      if (members.length < 2) continue; // skip singletons — no hull for a lone node
      out.push({ id: cid, color: clusterColourFor(cid), nodeIds: members });
    }
    return out;
  }, [clustering]);

  // Collapsed view: each cluster becomes one super-node (no hulls then).
  const rendered = useMemo(
    () => (collapse && clustering
      ? collapseClustersTransform(visible, clustering, { idPrefix: COLLAPSED_CLUSTER_ID_PREFIX })
      : visible),
    [collapse, clustering, visible],
  );
  const collapsedLookup = useMemo(
    () => (collapse && clustering ? collapsedClusterLookup(clustering) : null),
    [collapse, clustering],
  );
  const effectiveGroups = collapse ? [] : groups;
  // Compose colour sources: a collapsed view takes no per-node override; otherwise an
  // active cluster colour wins per node, falling back to the caller's nodeColor (which
  // applies to every node when no cluster mode is active). colourForType/Predicate are
  // forwarded straight through for the renderer's base colouring.
  const effectiveNodeColor = useMemo(() => {
    if (collapse) return undefined;
    if (clusterNodeColor && nodeColor) {
      return (node: GraphNode): string | undefined => clusterNodeColor(node) ?? nodeColor(node);
    }
    return clusterNodeColor ?? nodeColor;
  }, [collapse, clusterNodeColor, nodeColor]);

  // Click → reveal this node's antecedents/descendants and select it.
  const handleNodeClick = useCallback(
    (node: GraphNode) => {
      const backingIds = backingIdsForRenderedNode(node, collapsedLookup);
      const selectedId = backingIds.includes(selected)
        ? selected
        : (backingIds[0] ?? node.id);
      setSelected(selectedId);
      if (lazy && onRevealRequestRef.current) {
        // Lazy: fetch this node's neighbourhood from the host, merge it into the
        // working graph, then reveal the fetched nodes (bounded). Never loads whole.
        const requestVersion = beginRevealRequest();
        void onRevealRequestRef.current(backingIds, revealDepth)
          .then((fetched) => {
            if (!isCurrentRevealRequest(requestVersion)) return;
            const merged = mergeWorkingGraph(fetched);
            const mergedIndex = buildIndex(merged);
            setRevealState((prev) => {
              return {
                ...prev,
                ids: revealNearest(prev.ids, mergedIndex, backingIds, revealDepth, revealLimit, expandDirection),
              };
            });
          })
          .catch(() => { /* keep the current view on a fetch failure */ });
        return;
      }
      setRevealState((prev) => {
        return {
          ...prev,
          ids: revealNearest(prev.ids, index, backingIds, revealDepth, revealLimit, expandDirection),
        };
      });
    },
    [
      index,
      revealDepth,
      expandDirection,
      collapsedLookup,
      selected,
      revealLimit,
      lazy,
      mergeWorkingGraph,
      beginRevealRequest,
      isCurrentRevealRequest,
    ],
  );

  // Right-click → re-centre the explorer on this node (reset the reveal).
  const handleNodeRecenter = useCallback(
    (node: GraphNode) => {
      const backingIds = backingIdsForRenderedNode(node, collapsedLookup);
      const selectedId = backingIds.includes(selected)
        ? selected
        : (backingIds[0] ?? node.id);
      setSelected(selectedId);
      if (lazy && onRevealRequestRef.current) {
        // Lazy: re-centre = fetch this node's neighbourhood and reset the reveal to it.
        const requestVersion = beginRevealRequest();
        void onRevealRequestRef.current(backingIds, initialDepth)
          .then((fetched) => {
            if (!isCurrentRevealRequest(requestVersion)) return;
            const merged = mergeWorkingGraph(fetched);
            const mergedIndex = buildIndex(merged);
            setRevealState((prev) => {
              return {
                ...prev,
                ids: limitedReveal(neighborhood(mergedIndex, selectedId, { depth: initialDepth }), revealLimit, [selectedId]),
              };
            });
          })
          .catch(() => { /* keep the current view */ });
        return;
      }
      setRevealState((prev) => ({
        ...prev,
        ids: limitedReveal(neighborhood(index, selectedId, { depth: initialDepth }), revealLimit, [selectedId]),
      }));
    },
    [
      index,
      initialDepth,
      collapsedLookup,
      selected,
      revealLimit,
      lazy,
      mergeWorkingGraph,
      beginRevealRequest,
      isCurrentRevealRequest,
    ],
  );

  // Emit the cluster legend when clustering recomputes.
  const onClustersRef = useRef(onClusters);
  onClustersRef.current = onClusters;
  useEffect(() => {
    const cb = onClustersRef.current;
    if (!cb) return;
    if (!clustering) {
      cb([], effectiveClusterBy);
      return;
    }
    const info: ClusterInfo[] = [...clustering.clusters.entries()]
      .map(([id, members]) => ({
        id,
        color: clusterColourFor(id),
        size: members.length,
        dominantType: dominantType(members, index.nodeById),
      }))
      .sort((a, b) => b.size - a.size);
    cb(info, effectiveClusterBy);
  }, [clustering, effectiveClusterBy, index]);

  // Highlight the selected node + its direct neighbours (the
  // antecedent/descendant frontier) and any search hits present in view.
  const highlightIds = useMemo(() => {
    const ids = new Set<string>([selected]);
    for (const n of directAncestors(index, selected)) ids.add(n.id);
    for (const n of directDescendants(index, selected)) ids.add(n.id);
    if (searchHits) for (const id of searchHits) if (index.nodeById.has(id)) ids.add(id);
    return collapsedLookup
      ? new Set([...ids].map((id) => collapsedLookup.renderedIdByBackingId.get(id) ?? id))
      : ids;
  }, [index, selected, collapsedLookup, searchHits]);

  if (!hasFocus) {
    throw new Error(`LineageExplorer focusId "${focusId}" is not present in graph.nodes`);
  }

  return (
    <ThreadGraph
      renderer={renderer}
      theme={theme}
      layoutDirection={layoutDirection}
      nodes={rendered.nodes}
      edges={rendered.edges}
      highlightIds={highlightIds}
      searchActive={(searchHits?.size ?? 0) > 0}
      colourForType={colourForType}
      colourForPredicate={colourForPredicate}
      nodeColor={effectiveNodeColor}
      groups={effectiveGroups}
      sizeByDegree={sizeByDegree}
      flowParticles={flowParticles}
      labelMode={labelMode}
      onNodeClick={handleNodeClick}
      onNodeRightClick={handleNodeRecenter}
      onReady={onReady}
      width={width}
      height={height}
      style={style}
      className={className}
    />
  );
}

/** The most common node type among `members` (ties → first seen). */
function dominantType(members: string[], nodeById: Map<string, GraphNode>): string {
  const counts = new Map<string, number>();
  let best = "";
  let bestCount = 0;
  for (const id of members) {
    const t = nodeById.get(id)?.type;
    if (!t) continue;
    const c = (counts.get(t) ?? 0) + 1;
    counts.set(t, c);
    if (c > bestCount) {
      best = t;
      bestCount = c;
    }
  }
  return best;
}

function distinct(values: string[]): string[] {
  return [...new Set(values)];
}

/**
 * Union two canonical graphs for lazy accumulation — dedupe nodes by id and
 * edges by source/target/predicate, and recompute the type/predicate vocab.
 */
function mergeGraphs(base: Graph, add: Graph): Graph {
  const nodes = new Map(base.nodes.map((n) => [n.id, n]));
  for (const n of add.nodes) nodes.set(n.id, n);
  const edgeKey = (e: GraphEdge): string => `${e.source}${e.target}${e.predicate}`;
  const edges = new Map(base.edges.map((e) => [edgeKey(e), e]));
  for (const e of add.edges) {
    const k = edgeKey(e);
    if (!edges.has(k)) edges.set(k, e);
  }
  const nodeArr = [...nodes.values()];
  const edgeArr = [...edges.values()];
  return {
    nodes: nodeArr,
    edges: edgeArr,
    nodeTypes: distinct(nodeArr.map((n) => n.type)),
    edgePredicates: distinct(edgeArr.map((e) => e.predicate)),
  };
}

interface CollapsedLookup {
  renderedIdByBackingId: Map<string, string>;
  backingIdsByRenderedId: Map<string, string[]>;
}

interface FocusReset {
  lineageKey: string;
  focusId: string;
  initialDepth: number;
  revealLimit: number;
}

interface RevealState {
  ids: Set<string>;
  focusReset: FocusReset;
}

function collapsedClusterLookup(clustering: Clustering): CollapsedLookup {
  const renderedIdByBackingId = new Map<string, string>();
  const backingIdsByRenderedId = new Map<string, string[]>();
  for (const [clusterId, members] of clustering.clusters) {
    const renderedId = `${COLLAPSED_CLUSTER_ID_PREFIX}${clusterId}`;
    backingIdsByRenderedId.set(renderedId, members);
    for (const member of members) renderedIdByBackingId.set(member, renderedId);
  }
  return { renderedIdByBackingId, backingIdsByRenderedId };
}

function backingIdsForRenderedNode(node: GraphNode, lookup: CollapsedLookup | null): string[] {
  return lookup?.backingIdsByRenderedId.get(node.id) ?? [node.id];
}

function selectionKey(
  node: GraphNode,
  antecedents: GraphNode[],
  descendants: GraphNode[],
  cluster: LineageSelection["cluster"],
): string {
  return JSON.stringify([
    nodeSummary(node),
    antecedents.map(nodeSummary),
    descendants.map(nodeSummary),
    cluster ? [cluster.id, [...cluster.nodeIds].sort(), cluster.dominantType] : null,
  ]);
}

function sameFocusReset(a: FocusReset, b: FocusReset): boolean {
  return a.lineageKey === b.lineageKey &&
    a.focusId === b.focusId &&
    a.initialDepth === b.initialDepth &&
    a.revealLimit === b.revealLimit;
}

/** The cluster a node belongs to under the active clustering (or null). */
function clusterOf(
  id: string,
  clustering: Clustering | null,
  nodeById: Map<string, GraphNode>,
): { id: number; nodeIds: string[]; dominantType: string } | null {
  if (!clustering) return null;
  const cid = clustering.labelOf.get(id);
  if (cid === undefined) return null;
  const members = clustering.clusters.get(cid) ?? [id];
  return { id: cid, nodeIds: [...members], dominantType: dominantType(members, nodeById) };
}

/**
 * Cap a freshly-computed neighbourhood to `limit` nodes, always keeping the
 * `seeds` and otherwise preferring the closest (lowest hop depth) nodes. Keeps
 * a hub's thousand-strong neighbourhood from flooding the canvas in one reveal —
 * you see the nearest `limit` and expand further deliberately.
 */
function limitedReveal(result: LineageResult, limit: number, seeds: Iterable<string>): Set<string> {
  const out = new Set<string>(seeds);
  if (result.ids.size <= limit) {
    for (const id of result.ids) out.add(id);
    return out;
  }
  const rest = [...result.ids]
    .filter((id) => !out.has(id))
    .sort((a, b) => (result.depthOf.get(a) ?? 0) - (result.depthOf.get(b) ?? 0));
  for (const id of rest) {
    if (out.size >= limit) break;
    out.add(id);
  }
  return out;
}

function revealNearest(
  current: Set<string>,
  index: GraphIndex,
  seeds: string[],
  depth: number,
  limit: number,
  direction: LineageDirection,
): Set<string> {
  const next = new Set(current);
  for (const id of seeds) next.add(id);
  const candidates: Array<[string, number]> = [];
  for (const id of seeds) {
    const r = neighborhood(index, id, { depth, direction });
    for (const rid of r.ids) candidates.push([rid, r.depthOf.get(rid) ?? 0]);
  }
  candidates.sort((a, b) => a[1] - b[1]);
  let added = 0;
  for (const [rid] of candidates) {
    if (added >= limit) break;
    if (!next.has(rid)) { next.add(rid); added += 1; }
  }
  return next;
}

function lineageGraphKey(graph: Graph): string {
  // Cheap O(1) structural fingerprint used only to detect when the backing
  // graph is *replaced* (e.g. a refetch) so we re-centre. The previous version
  // JSON.stringify-d every node + edge — an O(N) main-thread cost that janks
  // hard as the graph grows toward millions. Collisions only matter if two
  // distinct backing graphs share counts AND endpoints (vanishingly rare for a
  // refetch); the worst case is merely a skipped re-centre.
  const { nodes, edges } = graph;
  const n = nodes.length;
  const e = edges.length;
  const firstNode = n > 0 ? nodes[0]!.id : "";
  const lastNode = n > 0 ? nodes[n - 1]!.id : "";
  const firstEdge = e > 0 ? `${edges[0]!.source}>${edges[0]!.target}` : "";
  const lastEdge = e > 0 ? `${edges[e - 1]!.source}>${edges[e - 1]!.target}` : "";
  return `${n}:${e}:${firstNode}:${lastNode}:${firstEdge}:${lastEdge}`;
}

function nodeSummary(node: GraphNode): [id: string, type: string, label: string] {
  return [node.id, node.type, node.label];
}

/**
 * Turn (overlapping) maximal cliques into a partition for colouring:
 * each node joins the largest clique it belongs to; nodes in no clique
 * become singletons.
 */
function cliqueClustering(graph: Graph): Clustering {
  const cliques = maximalCliques(graph, { minSize: 3 });
  const labelOf = new Map<string, number>();
  const clusters = new Map<number, string[]>();
  let next = 0;
  for (const clique of cliques) {
    const fresh = clique.filter((id) => !labelOf.has(id));
    if (fresh.length === 0) continue;
    const id = next++;
    clusters.set(id, fresh);
    for (const m of fresh) labelOf.set(m, id);
  }
  for (const n of graph.nodes) {
    if (labelOf.has(n.id)) continue;
    const id = next++;
    labelOf.set(n.id, id);
    clusters.set(id, [n.id]);
  }
  return { labelOf, clusters, count: clusters.size };
}
